// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
// --- 修正：从 @mui/material 导入 Button ---
import {
    AppBar, Toolbar, Typography, Container, Box, Tabs, Tab,
    CircularProgress, Alert, Button // <--- 添加 Button 到这里
} from '@mui/material';
import VideoUpload from './components/VideoUpload';
import RealtimeMonitor from './components/RealtimeMonitor';
import StudentEntry from './components/StudentEntry';
import StudentManagement from './components/StudentManagement';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';

// --- 后端 API 基础 URL ---
// !!! 注意：请根据你的实际后端服务地址修改这里 !!!
const API_BASE_URL = 'http://localhost:5001';

// TabPanel 组件 (保持不变)
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
     {/* 使用 Box 包裹并根据 value 控制 display */}
      <Box sx={{ p: 3, display: value === index ? 'block' : 'none' }}>
        {children}
      </Box>
    </div>
  );
}

// a11yProps 函数 (保持不变)
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function App() {
  const [currentTab, setCurrentTab] = useState(0);
  // --- 学生状态管理 ---
  const [students, setStudents] = useState([]); // 初始化为空数组
  const [isLoadingStudents, setIsLoadingStudents] = useState(false); // 加载状态
  const [fetchError, setFetchError] = useState(null); // 获取学生列表时的错误信息
  const [actionError, setActionError] = useState(null); // 添加/编辑/删除时的错误信息
  const [actionSuccess, setActionSuccess] = useState(null); // 添加/编辑/删除时的成功信息

  // --- 获取所有学生数据的函数 ---
  const fetchStudents = useCallback(async () => {
    setIsLoadingStudents(true);
    setFetchError(null); // 清除旧错误
    setActionError(null);
    setActionSuccess(null);
    console.log('正在从后端获取学生列表...');
    try {
      const response = await fetch(`${API_BASE_URL}/api/students`);
      if (!response.ok) {
        // 尝试解析错误信息
        let errorMsg = `HTTP 错误 ${response.status}`;
        try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorMsg;
        } catch(e) { /* 忽略 JSON 解析错误 */ }
        throw new Error(`获取学生列表失败: ${errorMsg}`);
      }
      const data = await response.json();
      console.log('成功获取学生列表:', data);
      // 后端返回的数据应该包含 studentId, name, faceImageUrl (服务器生成的URL)
      // 使用 studentId 作为 React 的 key (假设它是唯一的)
      setStudents(data.map(s => ({ ...s, id: s.studentId }))); // 将 studentId 映射为 id
    } catch (error) {
      console.error('获取学生列表时出错:', error);
      setFetchError(error.message || '无法连接到服务器或获取学生数据失败。');
      setStudents([]); // 出错时清空列表
    } finally {
      setIsLoadingStudents(false);
    }
  }, []); // useCallback 避免在子组件重渲染时不必要地重新创建函数

  // --- 组件挂载时获取学生列表 ---
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]); // 依赖于 fetchStudents 函数

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // 切换到学生管理 Tab 时，如果之前有错误，可以尝试重新加载
    if (newValue === 3 && fetchError) {
        fetchStudents();
    }
    // 清除之前的操作反馈信息
    setActionError(null);
    setActionSuccess(null);
  };

  // --- 添加学生处理函数 (调用后端 API) ---
  const handleAddStudent = async (newStudentData) => {
    // newStudentData 包含 { name, studentId, faceImageUrl (Base64) }
    setActionError(null);
    setActionSuccess(null);
    console.log('App: 准备向后端发送添加学生请求:', newStudentData.studentId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newStudentData), // 发送包含 Base64 图片的数据
      });

      const responseData = await response.json(); // 总是尝试解析 JSON

      if (!response.ok) {
        throw new Error(responseData.error || `添加学生失败 (HTTP ${response.status})`);
      }

      console.log('App: 后端成功添加学生，返回:', responseData);
      // responseData.student 包含后端返回的完整学生信息 (含服务器生成的 faceImageUrl)
      // 使用后端返回的数据更新状态，确保 ID 和 URL 是最新的
      setStudents(prevStudents => [
        ...prevStudents,
        { ...responseData.student, id: responseData.student.studentId } // 使用 studentId 作为 key
      ]);
      setActionSuccess(responseData.message || `学生 ${responseData.student.name} 添加成功！`);
      return true; // 指示 StudentEntry 操作成功

    } catch (error) {
      console.error('App: 添加学生时出错:', error);
      setActionError(error.message || '添加学生时发生未知错误。');
      return false; // 指示 StudentEntry 操作失败
    }
  };

  // --- 删除学生处理函数 (调用后端 API) ---
  // 参数 studentIdToDelete 现在是学生的学号 (studentId)
  const handleDeleteStudent = async (studentIdToDelete) => {
    setActionError(null);
    setActionSuccess(null);
    console.log(`App: 准备从后端删除学生，学号: ${studentIdToDelete}`);
    // 找到要删除的学生以显示名字（可选）
    const studentName = students.find(s => s.studentId === studentIdToDelete)?.name || studentIdToDelete;

    try {
      const response = await fetch(`${API_BASE_URL}/api/students/${studentIdToDelete}`, {
        method: 'DELETE',
      });

      const responseData = await response.json(); // 尝试解析 JSON

      if (!response.ok) {
        throw new Error(responseData.error || `删除学生失败 (HTTP ${response.status})`);
      }

      console.log('App: 后端成功删除学生');
      // 从本地状态中移除学生
      setStudents(prevStudents =>
        prevStudents.filter(student => student.studentId !== studentIdToDelete)
      );
      setActionSuccess(responseData.message || `学生 ${studentName} 已成功删除。`);

    } catch (error) {
      console.error('App: 删除学生时出错:', error);
      setActionError(error.message || '删除学生时发生未知错误。');
    }
  };

  // --- 编辑学生处理函数 (调用后端 API) ---
  // updatedStudentData 包含 { id(学号), name, studentId(学号), faceImageUrl(可能是新Base64或旧URL) }
  const handleEditStudent = async (updatedStudentData) => {
    setActionError(null);
    setActionSuccess(null);
    const studentIdToUpdate = updatedStudentData.studentId; // 使用学号作为 API 路径参数
    console.log(`App: 准备向后端发送更新学生请求，学号: ${studentIdToUpdate}`);

    // 准备发送到后端的数据体
    const requestBody = {
        name: updatedStudentData.name,
        studentId: updatedStudentData.studentId,
        // 检查 faceImageUrl 是否是新的 Base64 数据 (通常以 'data:image/' 开头)
        // 如果不是，说明用户没有更换图片，则不发送 faceImageUrl 字段
        faceImageUrl: updatedStudentData.faceImageUrl?.startsWith('data:image/')
                      ? updatedStudentData.faceImageUrl
                      : undefined // 如果是旧 URL 或 null，则不包含此字段
    };
     // 清理 undefined 字段
     Object.keys(requestBody).forEach(key => requestBody[key] === undefined && delete requestBody[key]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/students/${studentIdToUpdate}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json(); // 尝试解析 JSON

      if (!response.ok) {
        throw new Error(responseData.error || `更新学生信息失败 (HTTP ${response.status})`);
      }

      console.log('App: 后端成功更新学生，返回:', responseData);
      // 使用后端返回的更新后的学生数据更新本地状态
      setStudents(prevStudents =>
        prevStudents.map(student =>
          student.studentId === studentIdToUpdate
            ? { ...responseData.student, id: responseData.student.studentId } // 使用返回的数据，并设置 id
            : student
        )
      );
      setActionSuccess(responseData.message || `学生 ${responseData.student.name} 的信息已更新。`);
      return true; // 指示 EditStudentDialog 操作成功

    } catch (error) {
      console.error('App: 更新学生信息时出错:', error);
      setActionError(error.message || '更新学生信息时发生未知错误。');
      return false; // 指示 EditStudentDialog 操作失败
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <FaceRetouchingNaturalIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          学堂脉动—未来课堂智慧教学辅助系统v1.0
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="功能选择 Tab" centered>
            <Tab icon={<OndemandVideoIcon />} label="视频疲劳分析" {...a11yProps(0)} />
            <Tab icon={<CameraAltIcon />} label="实时疲劳监测" {...a11yProps(1)} />
            <Tab icon={<PersonAddIcon />} label="学生信息录入" {...a11yProps(2)} />
            <Tab icon={<PeopleIcon />} label="学生数据管理" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* 操作反馈信息显示区域 */}
        {actionError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
        {actionSuccess && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setActionSuccess(null)}>{actionSuccess}</Alert>}

        {/* Tab 内容面板 */}
        <TabPanel value={currentTab} index={0}>
          <VideoUpload apiBaseUrl={API_BASE_URL} /> {/* 传递 API URL */}
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <RealtimeMonitor apiBaseUrl={API_BASE_URL} /> {/* 传递 API URL */}
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
           {/* 传递 handleAddStudent (现在是异步函数) */}
          <StudentEntry onStudentAdd={handleAddStudent} />
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          {/* 显示加载状态或错误信息 */}
          {isLoadingStudents && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>正在加载学生列表...</Typography>
            </Box>
          )}
          {/* --- 修正：确保 Button 已导入 --- */}
          {fetchError && !isLoadingStudents && (
             <Alert severity="error" sx={{ mb: 2 }}>
                加载学生列表失败: {fetchError}
                {/* 现在 Button 应该可以正常工作了 */}
                <Button onClick={fetchStudents} size="small" sx={{ ml: 2 }}>重试</Button>
             </Alert>
          )}
          {!isLoadingStudents && !fetchError && (
            <StudentManagement
              students={students} // 传递从后端获取的学生列表
              onStudentDelete={handleDeleteStudent} // 传递删除处理函数
              onStudentEdit={handleEditStudent}     // 传递编辑处理函数
            />
          )}
        </TabPanel>
      </Container>
    </Box>
  );
}

export default App;